import React, { useState, useEffect, useRef } from 'react';

import { getCP3Env } from '../../../../CP3Config';

const minSupportedWidth = +getCP3Env('REACT_APP_MIN_SUPPORTED_WIDTH') || 320;

/**
 * When iOS Safari is open on a zoomed page/session, the innerWidth and innerHeight
 * values are not reliable. Because of this, we calculate the initial height based
 * on the constant proportion of the current sizes and supported sizes:
 *    SUPPORTED WIDTH / SUPPORTED HEIGHT = CURRENT WIDTH / CURRENT HEIGHT ...(1)
 *    SUPPORTED HEIGHT = SUPPORTED WIDTH * CURRENT HEIGHT / CURRENT WIDTH ...(2)
 */
const getInitialHeight = () => {
	if (window.innerWidth >= minSupportedWidth) {
		return window.innerHeight;
	}

	return Math.floor(
		(minSupportedWidth * window.innerHeight) / window.innerWidth
	);
};

/**
 * Component based on https://github.com/mvasin/react-div-100vh
 * This is a simpler component than the one on the library, we do
 * check the motivation on why that component exists on the readme.
 *
 * This implementation differs than the original:
 * https://github.com/mvasin/react-div-100vh/blob/master/src/lib/Div100vh.js
 * as we need to take extra measure for iOS devices firing
 * the resize event when scrolling, check https://stackoverflow.com/questions/8898412/iphone-ipad-triggering-unexpected-resize-events/24212316#24212316, we will
 * ignore resize events that tells us that the device width is lower than
 * supported (this happens when scrolling after zooming in on iPhones)
 */
const Div100vh = props => {
	const [style, setStyle] = useState({ height: `${getInitialHeight()}px` });
	const updateStyle = useRef(Function.prototype);

	useEffect(() => {
		updateStyle.current = () => {
			if (window.innerWidth >= minSupportedWidth) {
				setStyle({ height: `${window.innerHeight}px` });
			}
		};

		updateStyle.current();
		window.addEventListener('resize', updateStyle.current);
		return () => {
			window.removeEventListener('resize', updateStyle.current);
		};
	}, []);

	return <div {...props} style={style} />;
};

export default Div100vh;
